import { useApolloClient } from "@apollo/client";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import * as FSR from "src/generated/hooks/form-service/requests";
import { resetFormForMe } from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { evictFormCache } from "src/services/apolloCacheManager";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { EditMode } from "../EnforceFormEditability";
import { UPDATE_IN_PROGRESS_FORM } from "../graphql/mutations";

type Props = {
  formId?: string;
};

export function useSendFormBackToInProgress(props?: Props) {
  const flags = useFlags(["form-service-form-status"]);

  const gqlHook = useSendFormBackToInProgressGQL(props);
  const restHook = useSendFormBackToInProgressREST(props);

  if (flags["form-service-form-status"].enabled) {
    return restHook;
  } else {
    return gqlHook;
  }
}

function useSendFormBackToInProgressGQL(props?: Props) {
  const organization = useOrganization();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [isSendingFormToInProgress, setSendingFormToInProgress] =
    React.useState(false);
  const sendFormBackToInProgress = React.useCallback(
    async (dynamicFormId?: string) => {
      const formId = dynamicFormId ?? props?.formId;
      if (!formId) throw new Error("formId is required");

      setSendingFormToInProgress(true);
      await apolloClient.mutate<
        GQL.UpdateInProgressForm,
        GQL.UpdateInProgressFormVariables
      >({
        mutation: UPDATE_IN_PROGRESS_FORM,
        variables: {
          form_id: formId,
        },
        update: evictFormCache,
      });
      try {
        await FSR.createRuleExecutionsForMe({
          body: {
            data: {
              targets: [{ type: "form", formId }],
            },
          },
        });
      } catch (error) {
        console.warn("Error executing form rules:", error);
      }
      setSendingFormToInProgress(false);
      navigate(
        organization
          .map((org) => Url.Parent.Form.edit(org, formId))
          .withDefault("#"),
        { state: "BackInProgress" satisfies EditMode }
      );
    },
    [apolloClient, props?.formId, navigate, organization]
  );

  return { isSendingFormToInProgress, sendFormBackToInProgress };
}

function useSendFormBackToInProgressREST(props?: Props) {
  const organization = useOrganization();
  const navigate = useNavigate();
  const [isSendingFormToInProgress, setSendingFormToInProgress] =
    React.useState(false);
  const apolloClient = useApolloClient();

  const sendFormBackToInProgress = React.useCallback(
    async (dynamicFormId?: string) => {
      const formId = dynamicFormId ?? props?.formId;
      if (!formId) throw new Error("formId is required");

      setSendingFormToInProgress(true);
      await resetFormForMe({
        path: {
          formId: formId,
        },
      });
      await apolloClient.refetchQueries({
        updateCache: evictFormCache,
      });
      setSendingFormToInProgress(false);
      navigate(
        organization
          .map((org) => Url.Parent.Form.edit(org, formId))
          .withDefault("#"),
        { state: "BackInProgress" satisfies EditMode }
      );
    },
    [apolloClient, props?.formId, navigate, organization]
  );

  return { isSendingFormToInProgress, sendFormBackToInProgress };
}
